import { parseIntEnv } from "@/shared/lib/envUtils";

type CompanionDebugLogEntry = {
  ts: number;
  ms: number;
  label: string;
  detail?: string;
};

declare global {
  interface Window {
    __COAGENT_LOGS__?: CompanionDebugLogEntry[];
    __COAGENT_DEBUG__?: {
      enableConsoleLogs: () => void;
      disableConsoleLogs: () => void;
      getLogs: () => CompanionDebugLogEntry[];
      clearLogs: () => void;
    };
  }
}

const COAGENT_DEBUG_CONSOLE_ENABLED = import.meta.env.VITE_COAGENT_DEBUG_LOGS === "1";
const COAGENT_DEBUG_LOG_BUFFER_LIMIT = parseIntEnv(
  import.meta.env.VITE_COAGENT_LOG_BUFFER_LIMIT,
  500,
  50,
);

let sessionStartMs = Date.now();
let consoleEnabled = COAGENT_DEBUG_CONSOLE_ENABLED;
let logBuffer: CompanionDebugLogEntry[] = [];
let initialized = false;

export function initCompanionLogger(): void {
  if (initialized) return;
  initialized = true;
  sessionStartMs = Date.now();

  if (typeof window === "undefined") return;

  if (window.localStorage.getItem("coagent:debug-console") === "1") {
    consoleEnabled = true;
  }

  if (Array.isArray(window.__COAGENT_LOGS__) && window.__COAGENT_LOGS__.length > 0) {
    logBuffer = window.__COAGENT_LOGS__;
  } else {
    window.__COAGENT_LOGS__ = logBuffer;
  }

  const setConsoleLogs = (enabled: boolean) => {
    consoleEnabled = enabled;
    try {
      if (enabled) {
        window.localStorage.setItem("coagent:debug-console", "1");
      } else {
        window.localStorage.removeItem("coagent:debug-console");
      }
    } catch {
      // no-op
    }
  };

  window.__COAGENT_DEBUG__ = {
    enableConsoleLogs: () => setConsoleLogs(true),
    disableConsoleLogs: () => setConsoleLogs(false),
    getLogs: () => [...logBuffer],
    clearLogs: () => {
      logBuffer = [];
      window.__COAGENT_LOGS__ = logBuffer;
    },
  };
}

export function cleanupCompanionLogger(): void {
  if (typeof window === "undefined") return;
  if (window.__COAGENT_DEBUG__) {
    delete window.__COAGENT_DEBUG__;
  }
  initialized = false;
}

export type LogFn = (label: string, detail?: string) => void;

export function cpLog(label: string, detail?: string): void {
  const ms = Date.now() - sessionStartMs;
  const entry: CompanionDebugLogEntry = { ts: Date.now(), ms, label, detail };
  logBuffer.push(entry);
  if (logBuffer.length > COAGENT_DEBUG_LOG_BUFFER_LIMIT) {
    logBuffer.shift();
  }
  if (typeof window !== "undefined") {
    window.__COAGENT_LOGS__ = logBuffer;
  }

  if (!consoleEnabled) return;
  if (process.env.NODE_ENV !== "production")
    console.log(
      `%c[coagent:${label}]%c +${ms}ms${detail ? ` — ${detail}` : ""}`,
      "color:#7c3aed;font-weight:bold;font-family:monospace",
      "color:#6b7280;font-family:monospace",
    );
}
